// Persists the content of version-stamped Drive JSON files (e.g. info.json) across page loads, so an
// unchanged file costs no request after a reload. An entry is only served for the exact Drive
// `version` it was saved with, which makes it safe to trust indefinitely. Holds album titles and
// names, never credentials; cleared on logout.

import {JsonFileContent} from '../domain/json';

const STORAGE_KEY = 'spot-drive-json-files';
export const MAX_ENTRIES = 1000;
const FLUSH_DELAY_MS = 1000;

interface StoredEntry {
    /** Drive file version the text belongs to. */
    v: string;
    /** The file's content, minified. */
    t: string;
    /** Whether the file itself isn't minified. */
    b: boolean;
    /** When it was fetched or written (ms since epoch). */
    at: number;
}

let entries: Map<string, StoredEntry> | null = null;
let flushTimer: ReturnType<typeof setTimeout> | undefined;

function isStoredEntry(value: unknown): value is StoredEntry {
    const entry = value as StoredEntry | null;
    // Entries saved before the `b` flag existed may hold unminified text: they are dropped (and refetched).
    return !!entry && typeof entry.v === 'string' && typeof entry.t === 'string' && typeof entry.b === 'boolean'
        && typeof entry.at === 'number';
}

function loadEntries(): Map<string, StoredEntry> {
    if (entries) return entries;
    entries = new Map();
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const parsed: unknown = raw ? JSON.parse(raw) : null;
        if (parsed && typeof parsed === 'object') {
            for (const [fileId, entry] of Object.entries(parsed as Record<string, unknown>)) {
                if (isStoredEntry(entry)) entries.set(fileId, entry);
            }
        }
    } catch {
        // Unavailable or unreadable storage: start empty — files are simply fetched.
    }
    return entries;
}

function flush() {
    flushTimer = undefined;
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(loadEntries())));
    } catch {
        // Full or unavailable storage: the in-memory copy still serves this session.
    }
}

function evictOldest(map: Map<string, StoredEntry>) {
    let oldestId: string | undefined;
    let oldestAt = Infinity;
    map.forEach((entry, fileId) => {
        if (entry.at < oldestAt) {
            oldestAt = entry.at;
            oldestId = fileId;
        }
    });
    if (oldestId !== undefined) map.delete(oldestId);
}

/** The saved content of this exact file version, if we have it. */
export function readJsonFile(fileId: string, version: string): { content: JsonFileContent; savedAt: number } | undefined {
    const entry = loadEntries().get(fileId);
    return entry && entry.v === version ? {content: {text: entry.t, beautified: entry.b}, savedAt: entry.at} : undefined;
}

/** Remembers a file version's content (replacing older versions of the file); written out shortly after. */
export function writeJsonFile(fileId: string, version: string, content: JsonFileContent, savedAt: number = Date.now()): void {
    const map = loadEntries();
    map.set(fileId, {v: version, t: content.text, b: content.beautified, at: savedAt});
    while (map.size > MAX_ENTRIES) evictOldest(map);
    if (flushTimer === undefined) flushTimer = setTimeout(flush, FLUSH_DELAY_MS);
}

/** Forgets everything, e.g. on logout. */
export function clearJsonFiles(): void {
    if (flushTimer !== undefined) clearTimeout(flushTimer);
    flushTimer = undefined;
    entries = null;
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch {
        // Unavailable storage: nothing persisted to remove.
    }
}
